import { DELIVERY_METHODS } from "../enums/DeliveryMethods";
import { ENTITY_AVAILABILITY_STATUS } from "../enums/EntityAvailabilityStatus";
import { PRODUCT_TYPES } from "../enums/ProductTypes";
import { PURCHASE_SPLIT } from "../enums/PurchaseSplitOptions";
import { ROLE_TYPES } from "../enums/Roles";
import { STOCK_TYPES } from "../enums/StockTypes";
import { STOCK_AVAILABILITY_MODES } from "../enums/StockAvailabilityModes";
import { ENTITY_AVAILABILITY_STATUS_OPTIONS } from "./EntityAvailabilityStatusUtils";

// ===== Option Arrays =====
export const DELIVERY_METHODS_OPTIONS = [
  { id: DELIVERY_METHODS.TAKE_AWAY, name: "Retirar" },
  { id: DELIVERY_METHODS.ON_SITE, name: "En el salón" },
];

export const PRODUCT_TYPES_OPTIONS = [
  { id: PRODUCT_TYPES.REGULAR, name: "Regular" },
  { id: PRODUCT_TYPES.REWARD, name: "Premio" },
  { id: PRODUCT_TYPES.PURCHASE, name: "Compra" },
];

export const PURCHASE_SPLIT_OPTIONS = [
  { id: PURCHASE_SPLIT.FULL_AMOUNT, name: "90%" },
  { id: PURCHASE_SPLIT.RESTAURANT_PERCENTAGE, name: "La del restaurante" },
  { id: PURCHASE_SPLIT.CUSTOM_PERCENTAGE, name: "Personalizada" },
];

export const STOCK_TYPES_OPTIONS = [
  { id: STOCK_TYPES.UNLIMITED, name: "Ilimitado" },
  { id: STOCK_TYPES.LIMITED, name: "Limitado" },
];

export const STOCK_AVAILABILITY_MODES_OPTIONS = [
  { id: STOCK_AVAILABILITY_MODES.RENEWING, name: "Renovable" },
  { id: STOCK_AVAILABILITY_MODES.PER_DAY, name: "Por día" },
];

export const PREMIUM_PLAN_NAME = "PREMIUM";

export const DISCOUNT_DAYS_OPTIONS = [
  { id: "Mo", name: "Lunes" },
  { id: "Tu", name: "Martes" },
  { id: "We", name: "Miércoles" },
  { id: "Th", name: "Jueves" },
  { id: "Fr", name: "Viernes" },
  { id: "Sa", name: "Sábado" },
  { id: "Su", name: "Domingo" },
];

// ===== Discount Days Arrays =====
export const ALL_DAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
export const SUN_TO_TH = ["Su", "Mo", "Tu", "We", "Th"]; // ARGENTINA REGULAR PROMO AND HOT PROMO ARGENTINA
export const FRI_TO_SAT = ["Fr", "Sa"]; // ARGENTINA REGULAR PROMO
export const MON_TO_TH = ["Mo", "Tu", "We", "Th"]; // CHILE REGULAR PROMO
export const FRI_TO_SUN = ["Fr", "Sa", "Su"]; // CHILE REGULAR PROMO
export const MON_TO_FR = ["Mo", "Tu", "We", "Th", "Fr"];
export const MON_TO_SAT = ["Mo", "Tu", "We", "Th", "Fr", "Sa"]; // CHILE HOT PROMO

// ===== Discount Generation =====
export const generateDiscounts = (discountRules) => (planId) =>
  discountRules.map(({ days, percentage }) => ({
    days,
    discounts: [{ plan: planId, discount: percentage }],
  }));

// ===== Discount Percentages =====
const DISCOUNTS_PERCENTAGES = {
  FULL_AMOUNT: 1,
  FOURTY_PERCENT: 0.4,
  THIRTY_PERCENT: 0.3,
  TWENTY_FIVE_PERCENT: 0.25,
  TWENTY_PERCENT: 0.2,
  FIFTEEN_PERCENT: 0.15,
  FIFTY_PERCENT: 0.5,
};

// ===== Discount Generators =====

const REWARD_DISCOUNT = generateDiscounts([
  { days: ALL_DAYS, percentage: DISCOUNTS_PERCENTAGES.FULL_AMOUNT },
]);

// ARGENTINA
const REGULAR_PROMO_25_20 = generateDiscounts([
  { days: SUN_TO_TH, percentage: DISCOUNTS_PERCENTAGES.TWENTY_FIVE_PERCENT },
  { days: FRI_TO_SAT, percentage: DISCOUNTS_PERCENTAGES.TWENTY_PERCENT },
]);

const REGULAR_PROMO_20_15 = generateDiscounts([
  { days: SUN_TO_TH, percentage: DISCOUNTS_PERCENTAGES.TWENTY_PERCENT },
  { days: FRI_TO_SAT, percentage: DISCOUNTS_PERCENTAGES.FIFTEEN_PERCENT },
]);

const ALL_DAYS_20_PERCENT = generateDiscounts([
  { days: ALL_DAYS, percentage: DISCOUNTS_PERCENTAGES.TWENTY_PERCENT },
]);

const ALL_DAYS_15_PERCENT = generateDiscounts([
  { days: ALL_DAYS, percentage: DISCOUNTS_PERCENTAGES.FIFTEEN_PERCENT },
]);

const SUN_TO_TH_25_PERCENT = generateDiscounts([
  { days: SUN_TO_TH, percentage: DISCOUNTS_PERCENTAGES.TWENTY_FIVE_PERCENT },
]);

const ALL_DAYS_25_PERCENT = generateDiscounts([
  { days: ALL_DAYS, percentage: DISCOUNTS_PERCENTAGES.TWENTY_FIVE_PERCENT },
]);

const MO_TO_TH_30_PERCENT = generateDiscounts([
  { days: MON_TO_TH, percentage: DISCOUNTS_PERCENTAGES.THIRTY_PERCENT },
]);

const FRI_TO_SAT_25_PERCENT = generateDiscounts([
  { days: FRI_TO_SAT, percentage: DISCOUNTS_PERCENTAGES.TWENTY_FIVE_PERCENT },
]);

// CHILE
const CL_REGULAR_PROMO_25_20 = generateDiscounts([
  { days: MON_TO_TH, percentage: DISCOUNTS_PERCENTAGES.TWENTY_FIVE_PERCENT },
  { days: FRI_TO_SAT, percentage: DISCOUNTS_PERCENTAGES.TWENTY_PERCENT },
]);

const CL_REGULAR_PROMO_20_15 = generateDiscounts([
  { days: MON_TO_TH, percentage: DISCOUNTS_PERCENTAGES.TWENTY_PERCENT },
  { days: FRI_TO_SUN, percentage: DISCOUNTS_PERCENTAGES.FIFTEEN_PERCENT },
]);

const ALL_DAYS_40_PERCENT = generateDiscounts([
  { days: ALL_DAYS, percentage: DISCOUNTS_PERCENTAGES.FOURTY_PERCENT },
]);

const ALL_DAYS_30_PERCENT = generateDiscounts([
  { days: ALL_DAYS, percentage: DISCOUNTS_PERCENTAGES.THIRTY_PERCENT },
]);

const MO_TO_TH_15_PERCENT = generateDiscounts([
  { days: MON_TO_TH, percentage: DISCOUNTS_PERCENTAGES.FIFTEEN_PERCENT },
]);

const MO_TO_FR_15_PERCENT = generateDiscounts([
  { days: MON_TO_FR, percentage: DISCOUNTS_PERCENTAGES.FIFTEEN_PERCENT },
]);

const ALL_DAYS_50_PERCENT = generateDiscounts([
  { days: ALL_DAYS, percentage: DISCOUNTS_PERCENTAGES.FIFTY_PERCENT },
]);

// ===== Discount Keys & Options Mapping =====
const DISCOUNTS_PERCENTAGES_KEYS = {
  REWARD_DISCOUNT_KEY: "REWARD_DISCOUNT",
  REGULAR_PROMO_25_20_KEY: "REGULAR_PROMO_25_20",
  REGULAR_PROMO_20_15_KEY: "REGULAR_PROMO_20_15",
  ALL_DAYS_20_PERCENT_KEY: "ALL_DAYS_20_PERCENT",
  ALL_DAYS_15_PERCENT_KEY: "ALL_DAYS_15_PERCENT",
  ALL_DAYS_25_PERCENT_KEY: "ALL_DAYS_25_PERCENT",
  MO_TO_TH_30_PERCENT_KEY: "MO_TO_TH_30_PERCENT",
  FRI_TO_SAT_25_PERCENT_KEY: "FRI_TO_SAT_25_PERCENT",
  SUN_TO_TH_25_PERCENT_KEY: "SUN_TO_TH_25_PERCENT",
  // CHILE
  CL_REGULAR_PROMO_25_20_KEY: "CL_REGULAR_PROMO_25_20",
  CL_REGULAR_PROMO_20_15_KEY: "CL_REGULAR_PROMO_20_15",

  ALL_DAYS_40_PERCENT_KEY: "ALL_DAYS_40_PERCENT",
  ALL_DAYS_30_PERCENT_KEY: "ALL_DAYS_30_PERCENT",
  MO_TO_TH_15_PERCENT_KEY: "MO_TO_TH_15_PERCENT",
  MO_TO_FR_15_PERCENT_KEY: "MO_TO_FR_15_PERCENT",
  ALL_DAYS_50_PERCENT_KEY: "ALL_DAYS_50_PERCENT",
};

export const DISCOUNT_OPTIONS = {
  [DISCOUNTS_PERCENTAGES_KEYS.REGULAR_PROMO_25_20_KEY]: {
    id: DISCOUNTS_PERCENTAGES_KEYS.REGULAR_PROMO_25_20_KEY,
    name: "Regular AR 25 do-ju /20 vi-sa ",
  },
  [DISCOUNTS_PERCENTAGES_KEYS.REGULAR_PROMO_20_15_KEY]: {
    id: DISCOUNTS_PERCENTAGES_KEYS.REGULAR_PROMO_20_15_KEY,
    name: "Regular AR 20 do-ju /15 vi-sa",
  },
  [DISCOUNTS_PERCENTAGES_KEYS.ALL_DAYS_15_PERCENT_KEY]: {
    id: DISCOUNTS_PERCENTAGES_KEYS.ALL_DAYS_15_PERCENT_KEY,
    name: "15% todos los días",
  },
  [DISCOUNTS_PERCENTAGES_KEYS.ALL_DAYS_20_PERCENT_KEY]: {
    id: DISCOUNTS_PERCENTAGES_KEYS.ALL_DAYS_20_PERCENT_KEY,
    name: "20% todos los días",
  },
  [DISCOUNTS_PERCENTAGES_KEYS.ALL_DAYS_30_PERCENT_KEY]: {
    id: DISCOUNTS_PERCENTAGES_KEYS.ALL_DAYS_30_PERCENT_KEY,
    name: "30% todos los días",
  },
  [DISCOUNTS_PERCENTAGES_KEYS.REWARD_DISCOUNT_KEY]: {
    id: DISCOUNTS_PERCENTAGES_KEYS.REWARD_DISCOUNT_KEY,
    name: "Premio - 100% Todos los días",
  },
  [DISCOUNTS_PERCENTAGES_KEYS.MO_TO_TH_30_PERCENT_KEY]: {
    id: DISCOUNTS_PERCENTAGES_KEYS.MO_TO_TH_30_PERCENT_KEY,
    name: "Hot 30% lunes a jueves",
  },
  [DISCOUNTS_PERCENTAGES_KEYS.SUN_TO_TH_25_PERCENT_KEY]: {
    id: DISCOUNTS_PERCENTAGES_KEYS.SUN_TO_TH_25_PERCENT_KEY,
    name: "Hot 25% domingo a jueves",
  },
  [DISCOUNTS_PERCENTAGES_KEYS.ALL_DAYS_25_PERCENT_KEY]: {
    id: DISCOUNTS_PERCENTAGES_KEYS.ALL_DAYS_25_PERCENT_KEY,
    name: "Hot 25% todos los días",
  },
  [DISCOUNTS_PERCENTAGES_KEYS.ALL_DAYS_50_PERCENT_KEY]: {
    id: DISCOUNTS_PERCENTAGES_KEYS.ALL_DAYS_50_PERCENT_KEY,
    name: "Hot 50% todos los días",
  },
  // CHILE
  [DISCOUNTS_PERCENTAGES_KEYS.CL_REGULAR_PROMO_25_20_KEY]: {
    id: DISCOUNTS_PERCENTAGES_KEYS.CL_REGULAR_PROMO_25_20_KEY,
    name: "Regular CL 25 lu-ju / 20 vi-do ",
  },
  [DISCOUNTS_PERCENTAGES_KEYS.CL_REGULAR_PROMO_20_15_KEY]: {
    id: DISCOUNTS_PERCENTAGES_KEYS.CL_REGULAR_PROMO_20_15_KEY,
    name: "Regular CL 20 lu-ju /15 vi-do",
  },
  [DISCOUNTS_PERCENTAGES_KEYS.FRI_TO_SAT_25_PERCENT_KEY]: {
    id: DISCOUNTS_PERCENTAGES_KEYS.FRI_TO_SAT_25_PERCENT_KEY,
    name: "25% viernes y sábado",
  },

  [DISCOUNTS_PERCENTAGES_KEYS.ALL_DAYS_40_PERCENT_KEY]: {
    id: DISCOUNTS_PERCENTAGES_KEYS.ALL_DAYS_40_PERCENT_KEY,
    name: "40% todos los días",
  },
  [DISCOUNTS_PERCENTAGES_KEYS.MO_TO_TH_15_PERCENT_KEY]: {
    id: DISCOUNTS_PERCENTAGES_KEYS.MO_TO_TH_15_PERCENT_KEY,
    name: "15% lunes a jueves",
  },
  [DISCOUNTS_PERCENTAGES_KEYS.MO_TO_FR_15_PERCENT_KEY]: {
    id: DISCOUNTS_PERCENTAGES_KEYS.MO_TO_FR_15_PERCENT_KEY,
    name: "15% lunes a viernes",
  },
};

export const DISCOUNT_MAPPER = {
  [DISCOUNTS_PERCENTAGES_KEYS.REWARD_DISCOUNT_KEY]: REWARD_DISCOUNT,
  [DISCOUNTS_PERCENTAGES_KEYS.REGULAR_PROMO_25_20_KEY]: REGULAR_PROMO_25_20,
  [DISCOUNTS_PERCENTAGES_KEYS.REGULAR_PROMO_20_15_KEY]: REGULAR_PROMO_20_15,
  [DISCOUNTS_PERCENTAGES_KEYS.ALL_DAYS_20_PERCENT_KEY]: ALL_DAYS_20_PERCENT,
  [DISCOUNTS_PERCENTAGES_KEYS.ALL_DAYS_15_PERCENT_KEY]: ALL_DAYS_15_PERCENT,
  [DISCOUNTS_PERCENTAGES_KEYS.ALL_DAYS_30_PERCENT_KEY]: ALL_DAYS_30_PERCENT,
  [DISCOUNTS_PERCENTAGES_KEYS.ALL_DAYS_25_PERCENT_KEY]: ALL_DAYS_25_PERCENT,
  [DISCOUNTS_PERCENTAGES_KEYS.MO_TO_TH_30_PERCENT_KEY]: MO_TO_TH_30_PERCENT,
  [DISCOUNTS_PERCENTAGES_KEYS.FRI_TO_SAT_25_PERCENT_KEY]: FRI_TO_SAT_25_PERCENT,
  [DISCOUNTS_PERCENTAGES_KEYS.SUN_TO_TH_25_PERCENT_KEY]: SUN_TO_TH_25_PERCENT,
  // CHILE
  [DISCOUNTS_PERCENTAGES_KEYS.CL_REGULAR_PROMO_25_20_KEY]:
    CL_REGULAR_PROMO_25_20,
  [DISCOUNTS_PERCENTAGES_KEYS.CL_REGULAR_PROMO_20_15_KEY]:
    CL_REGULAR_PROMO_20_15,

  [DISCOUNTS_PERCENTAGES_KEYS.ALL_DAYS_40_PERCENT_KEY]: ALL_DAYS_40_PERCENT,
  [DISCOUNTS_PERCENTAGES_KEYS.MO_TO_TH_15_PERCENT_KEY]: MO_TO_TH_15_PERCENT,
  [DISCOUNTS_PERCENTAGES_KEYS.MO_TO_FR_15_PERCENT_KEY]: MO_TO_FR_15_PERCENT,
  [DISCOUNTS_PERCENTAGES_KEYS.ALL_DAYS_50_PERCENT_KEY]: ALL_DAYS_50_PERCENT,
};

// ===== Product Status Generator =====
export const getProductStatusOptionsByRole = (role) => {
  if (role === ROLE_TYPES.ADMIN) {
    return ENTITY_AVAILABILITY_STATUS_OPTIONS;
  }

  return ENTITY_AVAILABILITY_STATUS_OPTIONS.filter(
    (status) => status.id !== ENTITY_AVAILABILITY_STATUS.ARCHIVED
  );
};

// ===== Helper Functions =====
export const getDistinctDaysFomObject = (object) =>
  new Set(object.flatMap((item) => item.days));

export const filterTimes = (dataToFilter, daysToFilter) =>
  dataToFilter
    .map((entry) => {
      const keys = Object.keys(entry);
      if (keys.length < 2) return null;
      const [daysKey, valuesKey] = keys;
      const days = entry[daysKey];
      const values = entry[valuesKey];

      const filteredDays = days.filter((day) => daysToFilter.has(day));
      if (filteredDays.length === 0) return null;

      const filteredValues = values.filter(() =>
        filteredDays.some((day) => days.includes(day))
      );

      if (filteredValues.length === 0) return null;

      return { days: filteredDays, [valuesKey]: filteredValues };
    })
    .filter(Boolean);
